import isNull from "lodash/isNull";
import isUndefined from "lodash/isUndefined";
import get from "lodash/get";
import qs from "query-string";
import moment from "moment";
import { getBaseUrl, isServer } from "..";
import { API_ERROR_MESSAGES } from "../../constants/api";
import { ContextStorage } from "../../libs/contextStorage";

export const getDeviceInfo = () => !isServer && get(window, "ua", "");

/**
 * Get user agent from various sources
 * @param error - Optional error object to extract user agent from headers
 * @returns User agent string or "unknown" if not available
 */
export const getUserAgent = (error?: any): string => {
  // Try to get from error config headers first (if error object provided)
  if (error) {
    const errorUserAgent =
      get(error, "config.headers['User-Agent']") ||
      get(error, "config.headers['user-agent']");
    if (errorUserAgent) {
      return errorUserAgent;
    }
  }

  // Try to get from Redux store
  try {
    const store = ContextStorage.store;
    if (store) {
      const userAgent = store.getState()?.common?.userAgent;
      if (userAgent) {
        return userAgent;
      }
    }
  } catch (e) {
    // Store might not be available
  }

  // Fallback to browser navigator (client-side only)
  if (typeof window !== "undefined" && window.navigator) {
    return window.navigator.userAgent;
  }

  return "unknown";
};

export const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    const timerId = setTimeout(() => {
      resolve();

      clearTimeout(timerId);
    }, ms);
  });

export const isLinkStartsFromHTTPS = (link = "") => link.startsWith("https://");

export const isNil = value => Boolean(isUndefined(value) || isNull(value));

export const getRedirectionUrl = (isExternalLink, redirectionPath) =>
  isExternalLink ? { pathname: redirectionPath } : redirectionPath;

export const retryCall = ({ command, retry = 3, delay = 1000 }) => {
  let currentRetryCount = retry;

  const updatedCommand = async () => {
    try {
      currentRetryCount--;

      return await command();
    } catch (error) {
      if (!currentRetryCount) throw error;

      if (delay) await sleep(delay);

      return updatedCommand();
    }
  };

  return updatedCommand();
};

export const getDataAsObject = data =>
  typeof data === "object" ? data : JSON.parse(data);

export const truncateProductTitle = (title, characters) => {
  if (title && title.length > characters)
    return title.substring(0, characters) + "...";
  else {
    return title;
  }
};

export const isDateInPast = date => moment(date).isBefore(new Date());
export const isDateInFuture = date => moment(date).isAfter(new Date());

export const getReplacedPreviewHTML = (html = "", paymentCurrency = "AED") =>
  html && paymentCurrency && html.replace("{CURRENCY_CODE}", paymentCurrency);

export const isVideoLink = url =>
  /\.(mp4|webm|ogg|youtube\.com|vimeo\.com)/.test(url);

export const handleGoBack = (location, history, language, commonSettings) => {
  const { gclid } = qs.parse(location.search);
  const goHome = history.length <= 2 || gclid;
  if (goHome) {
    history.push(getBaseUrl({ language, commonSettings }) + "/");
  } else {
    history.goBack();
  }
};

export const checkResponseStatus = response => {
  const { response: nestedResponse } = response;

  if (response.message === API_ERROR_MESSAGES.NETWORK_ERROR) {
    return {
      isNetworkError: true,
      message: API_ERROR_MESSAGES.NETWORK_ERROR
    };
  } else if (nestedResponse?.data) {
    const statusCode = nestedResponse.data.statusCode;
    const message = nestedResponse.data?.message;

    switch (statusCode) {
      case 400:
      case 422:
        return { isToastMessage: true, isAlertMessage: true, message };

      default:
        return {};
    }
  }
  return {};
};

export const runSafely = async <T = any>(fn: () => T): Promise<T> => {
  try {
    return await fn();
  } catch {
    return undefined;
  }
};

export const getFormattedPhone = (fullPhone, mobileCountryCode) =>
  mobileCountryCode && fullPhone?.startsWith(mobileCountryCode)
    ? `${mobileCountryCode} ${fullPhone?.slice(
        mobileCountryCode.toString()?.length
      )}`
    : fullPhone;

/**
 * Higher-order function for caching Promise results of async functions
 * Cache is stored in closure and automatically cleared after request completion
 * @param fn - async function to cache
 * @param config - configuration object
 * @param config.key - optional function to generate cache key from arguments (defaults to empty string for functions without arguments)
 * @param config.ms - optional delay in milliseconds before clearing cache after request completion
 * @returns wrapped function with caching
 */
export function createPromiseCachedFunction<
  T extends (...args: any[]) => Promise<any>
>(
  fn: T,
  config?: {
    key?: (...args: Parameters<T>) => string;
    ms?: number;
  }
): T {
  const cache = new Map<
    string,
    Promise<ReturnType<T> extends Promise<infer U> ? U : never>
  >();
  const timers = new Map<string, NodeJS.Timeout>();

  return ((...args: Parameters<T>) => {
    const cacheKey = config?.key ? config.key(...args) : "default";
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }

    const requestPromise = (async () => {
      try {
        const result = await fn(...args);

        if (config?.ms !== undefined) {
          const existingTimer = timers.get(cacheKey);
          if (existingTimer) clearTimeout(existingTimer);

          const timer = setTimeout(() => {
            cache.delete(cacheKey);
            timers.delete(cacheKey);
          }, config.ms);
          timers.set(cacheKey, timer);
        }

        return result;
      } catch (error) {
        const existingTimer = timers.get(cacheKey);
        if (existingTimer) clearTimeout(existingTimer);
        cache.delete(cacheKey);
        timers.delete(cacheKey);
        throw error;
      }
    })();

    cache.set(cacheKey, requestPromise);
    return requestPromise;
  }) as T;
}
